// src/screens/Libros/Libros.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Libros.css';
import { Link, useHistory } from 'react-router-dom';
import client from '../../sanity/SanityClient';
import imageUrlBuilder from '@sanity/image-url';

const builder = imageUrlBuilder(client);
const urlFor = (source) =>
  builder.image(source).auto('format').fit('crop').width(1200).url();

// Trae TODOS los libros (sin drafts) y resuelve una sola URL "coverUrl"
const LIBROS_QUERY = `
  *[
    _type == "libro" &&
    !(_id in path("drafts.**"))
  ] | order(_createdAt desc) {
    _id,
    titulo,
    descripcion,
    // una sola URL de portada tomando la primera disponible
    "coverUrl": coalesce(
      previewImage.asset->url,
      detalleImage.asset->url,
      detalleImage2.asset->url,
      detalleImage3.asset->url
    ),
    // por si algún doc trae objeto image pero sin dereferenciar
    previewImage
  }
`;

function splitAttribution(text = '') {
  const m = text.match(/\s*\[Extracto de ([^\]]+)\]\s*$/i);
  if (!m) return { body: text, attribution: null };
  return { body: text.replace(m[0], '').trim(), attribution: m[1].trim() };
}

const Libros = () => {
  const history = useHistory();

  const [libros, setLibros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(1);
  const [isAnimating, setIsAnimating] = useState(true);
  const trackRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const res = await client.fetch(LIBROS_QUERY);

        const normalized = (res || [])
          .map((d) => {
            const src =
              d?.coverUrl ||
              (d?.previewImage ? urlFor(d.previewImage) : null);
            if (!src) return null;
            return {
              id: d._id,
              imagen: src,
              titulo: d.titulo || '',
              descripcion: d.descripcion || '',
            };
          })
          .filter(Boolean);

        if (!alive) return;
        setLibros(normalized);
        setCurrentIndex(normalized.length ? 1 : 0);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || 'Error al cargar libros');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const extendedLibros = useMemo(() => {
    if (!libros.length) return [];
    const first = libros[0];
    const last = libros[libros.length - 1];
    return [last, ...libros, first];
  }, [libros]);

  const totalSlides = libros.length;

  const nextSlide = () => {
    if (!totalSlides) return;
    if (currentIndex >= totalSlides + 1) return;
    setCurrentIndex((i) => i + 1);
  };
  const prevSlide = () => {
    if (!totalSlides) return;
    if (currentIndex <= 0) return;
    setCurrentIndex((i) => i - 1);
  };

  useEffect(() => {
    if (!totalSlides) return;
    const it = setInterval(nextSlide, 8000);
    return () => clearInterval(it);
  }, [totalSlides]);

  const handleTransitionEnd = () => {
    if (!totalSlides) return;
    if (currentIndex === 0) {
      setIsAnimating(false);
      setCurrentIndex(totalSlides);
    } else if (currentIndex === totalSlides + 1) {
      setIsAnimating(false);
      setCurrentIndex(1);
    }
  };
  useEffect(() => {
    if (!isAnimating) {
      const t = setTimeout(() => setIsAnimating(true), 20);
      return () => clearTimeout(t);
    }
  }, [isAnimating]);

  const goDetalle = (id) => { if (id) history.push(`/detallelibros/${id}`); };

  return (
    <section className="libros-wrapper">
      <h1 className="libros-titulo">Mis Libros</h1>
      <div className="libros-quote">
        “Fotografiar es poner en la misma línea la cabeza, el ojo y el corazón.” – Henri Cartier-Bresson
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', color: '#666', margin: '24px 0' }}>Cargando libros…</p>
      ) : err ? (
        <p style={{ textAlign: 'center', color: '#c00', margin: '24px 0' }}>{err}</p>
      ) : !totalSlides ? (
        <p style={{ textAlign: 'center', color: '#666', margin: '24px 0' }}>Sin libros por el momento.</p>
      ) : (
        <div className="libros-row">
          <button className="libros-arrow libros-left" onClick={prevSlide} aria-label="Anterior">‹</button>

          <div className="libros-track-viewport">
            <div
              ref={trackRef}
              className={`libros-track ${isAnimating ? 'animate' : ''}`}
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              onTransitionEnd={handleTransitionEnd}
            >
              {extendedLibros.map((libro, idx) => {
                const { body, attribution } = splitAttribution(libro.descripcion || '');
                const id = libro.id;
                const detailHref = id ? `/detallelibros/${id}` : undefined;

                return (
                  <div className="libros-slide" key={`${id || 'clone'}-${idx}`}>
                    <div className="libros-media">
                      {detailHref ? (
                        <Link to={detailHref} aria-label={`Detalle de ${libro.titulo}`}>
                          <img src={libro.imagen} alt={libro.titulo || 'Libro'} />
                        </Link>
                      ) : (
                        <img src={libro.imagen} alt={libro.titulo || 'Libro'} />
                      )}
                    </div>

                    <div className="libros-texto">
                      {detailHref ? (
                        <Link to={detailHref} className="libros-title-link" aria-label={`Detalle de ${libro.titulo}`}>
                          <h2 className="libros-title">{libro.titulo}</h2>
                        </Link>
                      ) : (
                        <h2 className="libros-title">{libro.titulo}</h2>
                      )}

                      <p className="libros-desc">{body}</p>
                      {attribution && <span className="libros-attr"> [Extracto de {attribution}]</span>}

                      <div className="libros-cta">
                        <button className="libros-btn" onClick={() => goDetalle(id)} disabled={!id}>Detalle</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button className="libros-arrow libros-right" onClick={nextSlide} aria-label="Siguiente">›</button>
        </div>
      )}
    </section>
  );
};

export default Libros;
